import { useState, useCallback } from 'react'

import type { BillingInterval } from '../../../gql/graphql'

import { BillingIntervalValues } from '../types'

/**
 * State for plan selection
 */
export interface PlanSelectionState {
  selectedChannelCount: number
  selectedBillingCycle: BillingInterval
}

/**
 * Actions for updating plan selection
 */
export interface PlanSelectionActions {
  selectChannels: (count: number) => void
  selectBillingCycle: (cycle: BillingInterval) => void
}

/**
 * Combined return type for the hook
 */
export interface UsePlanSelectionReturn
  extends PlanSelectionState, PlanSelectionActions {}

/**
 * Props for the plan selection hook
 */
interface UsePlanSelectionProps {
  initialChannelCount: number
  initialBillingCycle?: BillingInterval
}

/**
 * Hook for managing plan selection state
 * Handles channel count, billing cycle, and plan selection
 */
export function usePlanSelection({
  initialChannelCount,
  initialBillingCycle = BillingIntervalValues.year,
}: UsePlanSelectionProps): UsePlanSelectionReturn {
  const [selectedChannelCount, setSelectedChannelCount] =
    useState(initialChannelCount)

  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingInterval>(initialBillingCycle)

  const selectChannels = useCallback((count: number) => {
    setSelectedChannelCount(count)
  }, [])

  const selectBillingCycle = useCallback((cycle: BillingInterval) => {
    setSelectedBillingCycle(cycle)
  }, [])

  return {
    selectedChannelCount,
    selectedBillingCycle,
    selectChannels,
    selectBillingCycle,
  }
}
